import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { WeddingEvent, EventStatus } from '@/lib/types';
import { api } from '@/lib/api';

export const eventKeys = {
  all: (weddingId: string) => ['events', weddingId] as const,
};

export function useEvents(weddingId: string | null | undefined, options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: eventKeys.all(weddingId ?? ''),
    queryFn: () => api.get<WeddingEvent[]>(`/api/weddings/${weddingId}/events`),
    enabled: !!weddingId,
    staleTime: 10_000,
    refetchInterval: options?.refetchInterval,
    select: (events) => [...events].sort((a, b) =>
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    ),
  });
}

export function useCreateEvent(weddingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<WeddingEvent, 'id' | 'weddingId' | 'createdAt' | 'updatedAt'>) =>
      api.post<WeddingEvent>(`/api/weddings/${weddingId}/events`, data),
    onMutate: async (newEventData) => {
      await queryClient.cancelQueries({ queryKey: eventKeys.all(weddingId) });
      const previous = queryClient.getQueryData<WeddingEvent[]>(eventKeys.all(weddingId));
      // Optimistic: add a temporary event
      const optimistic: WeddingEvent = {
        ...newEventData,
        id: `temp-${Date.now()}`,
        weddingId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      queryClient.setQueryData<WeddingEvent[]>(eventKeys.all(weddingId), (old) => [
        ...(old ?? []),
        optimistic,
      ]);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(eventKeys.all(weddingId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all(weddingId) });
    },
  });
}

export function useUpdateEvent(weddingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: Partial<WeddingEvent> }) =>
      api.put<WeddingEvent>(`/api/weddings/${weddingId}/events/${eventId}`, data),
    onMutate: async ({ eventId, data }) => {
      await queryClient.cancelQueries({ queryKey: eventKeys.all(weddingId) });
      const previous = queryClient.getQueryData<WeddingEvent[]>(eventKeys.all(weddingId));
      queryClient.setQueryData<WeddingEvent[]>(eventKeys.all(weddingId), (old) =>
        (old ?? []).map((e) => (e.id === eventId ? { ...e, ...data } : e))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(eventKeys.all(weddingId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all(weddingId) });
    },
  });
}

export function useDeleteEvent(weddingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      api.delete<{ deleted: boolean }>(`/api/weddings/${weddingId}/events/${eventId}`),
    onMutate: async (eventId) => {
      await queryClient.cancelQueries({ queryKey: eventKeys.all(weddingId) });
      const previous = queryClient.getQueryData<WeddingEvent[]>(eventKeys.all(weddingId));
      queryClient.setQueryData<WeddingEvent[]>(eventKeys.all(weddingId), (old) =>
        (old ?? []).filter((e) => e.id !== eventId)
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(eventKeys.all(weddingId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all(weddingId) });
    },
  });
}

export function useUpdateEventStatus(weddingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, status }: { eventId: string; status: EventStatus }) =>
      api.patch<WeddingEvent>(`/api/weddings/${weddingId}/events/${eventId}/status`, { status }),
    onMutate: async ({ eventId, status }) => {
      await queryClient.cancelQueries({ queryKey: eventKeys.all(weddingId) });
      const previous = queryClient.getQueryData<WeddingEvent[]>(eventKeys.all(weddingId));
      queryClient.setQueryData<WeddingEvent[]>(eventKeys.all(weddingId), (old) =>
        (old ?? []).map((e) => (e.id === eventId ? { ...e, status } : e))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(eventKeys.all(weddingId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all(weddingId) });
    },
  });
}

export function useBulkCreateEvents(weddingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (events: Omit<WeddingEvent, 'id' | 'weddingId' | 'createdAt' | 'updatedAt'>[]) =>
      api.post<WeddingEvent[]>(`/api/weddings/${weddingId}/events/bulk`, { events }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all(weddingId) });
    },
  });
}
